import React, { useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { LuGripVertical } from "react-icons/lu";
import styles from "./DataTable.style";

export interface DataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T, index: number) => React.ReactNode;
  width?: number | string;
  align?: "start" | "center" | "end";
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => React.Key;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  /** Adds a drag handle column and makes rows draggable */
  sortable?: boolean;
  /** Called with the new row order each time a dragged row moves */
  onReorder?: (rows: T[]) => void;
}

/**
 * Bootstrap table with the dashboard look (light dividers, compact text).
 * Reordering uses native HTML5 drag and drop, so it needs a mouse.
 */
function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyMessage = "Nothing to show yet.",
  sortable = false,
  onReorder,
}: DataTableProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const moveRow = (to: number) => {
    if (dragIndex === null || dragIndex === to || !onReorder) return;
    const next = [...rows];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(to, 0, moved);
    setDragIndex(to);
    onReorder(next);
  };

  const colCount = columns.length + (sortable ? 1 : 0);
  const alignClass = (align?: DataTableColumn<T>["align"]) =>
    align ? `text-${align}` : "";

  return (
    <Table responsive className="align-middle mb-0" style={styles.table}>
      <thead>
        <tr>
          {sortable && <th style={{ ...styles.headerCell, ...styles.handle }} />}
          {columns.map((col) => (
            <th
              key={col.key}
              className={alignClass(col.align)}
              style={{ ...styles.headerCell, width: col.width }}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={colCount} className="text-center py-5">
              <Spinner animation="border" size="sm" />
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={colCount} className="text-center text-secondary py-5 small">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, index) => (
            <tr
              key={rowKey(row)}
              draggable={sortable}
              onDragStart={sortable ? () => setDragIndex(index) : undefined}
              onDragEnter={sortable ? () => moveRow(index) : undefined}
              onDragOver={sortable ? (e) => e.preventDefault() : undefined}
              onDragEnd={sortable ? () => setDragIndex(null) : undefined}
              style={{ opacity: dragIndex === index ? 0.4 : 1 }}
            >
              {sortable && (
                <td style={{ ...styles.cell, ...styles.handle }}>
                  <LuGripVertical size={16} />
                </td>
              )}
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={alignClass(col.align)}
                  style={styles.cell}
                >
                  {col.render(row, index)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
}

export default DataTable;
